import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Download,
  FileCheck2,
  HardHat,
  Search,
  ShieldCheck,
  Vote,
  X,
} from "lucide-react";
import { useAuth } from "../auth";
import { Card, PageHeader, cx } from "../ui";

// Rule-section IDs that anchor the Construction Rules tab; visiting
// /help#<id> jumps to the rules tab and scrolls there. Kept in sync
// with src/services/buildingRules.ts and the <Card id> attributes
// below.
const RULES_HASH_IDS = new Set([
  "categories",
  "working-hours",
  "insurance",
  "plumbing-electrical",
  "hard-surface-flooring",
  "washer-dryer",
  "wall-modifications",
  "shutdowns",
  "agreements",
]);
const FEES_HASH_IDS = new Set(["fees"]);

type RoleKey =
  | "unit_owner"
  | "property_manager"
  | "admin"
  | "board_member"
  | "chief_engineer";

const ROLE_LABEL: Record<RoleKey, string> = {
  unit_owner: "Unit Owner",
  property_manager: "Property Manager",
  admin: "Administrator",
  board_member: "Board Member",
  chief_engineer: "Chief Engineer",
};

type TabKey = RoleKey | "common" | "rules" | "fees";

type GuideTopic = {
  id: string;
  tab: TabKey;
  title: string;
  summary: string;
  keywords: string;
  icon: typeof BookOpen;
};

const GUIDE_TOPICS: GuideTopic[] = [
  {
    id: "getting-started",
    tab: "common",
    title: "Portal basics",
    summary: "Understand project categories, working hours, and each participant's role.",
    keywords: "getting started basics category hours navigation portal",
    icon: BookOpen,
  },
  {
    id: "submit-project",
    tab: "unit_owner",
    title: "Create and submit a project",
    summary: "Start a project, describe the work, add contractors, and submit for review.",
    keywords: "owner new project draft submit documents contractor scope",
    icon: FileCheck2,
  },
  {
    id: "contractor-lookup",
    tab: "common",
    title: "Find and share a contractor",
    summary: "Search prior building contractors, review job history, ratings, and contact details.",
    keywords: "contractor lookup directory history rating share contact",
    icon: HardHat,
  },
  {
    id: "insurance-guide",
    tab: "unit_owner",
    title: "Maintain owner insurance",
    summary: "Add or renew the required personal liability policy and avoid lapse fines.",
    keywords: "insurance certificate policy renewal fine owner liability",
    icon: ShieldCheck,
  },
  {
    id: "management-review",
    tab: "property_manager",
    title: "Review the management queue",
    summary: "Start review, return revisions, approve Category A, or route work to the Board.",
    keywords: "property manager queue review revisions approve route board",
    icon: FileCheck2,
  },
  {
    id: "board-voting",
    tab: "board_member",
    title: "Cast a Board vote",
    summary: "Review Category B and C projects and understand voting privacy and deadlines.",
    keywords: "board vote quorum decision approve reject recuse deadline",
    icon: Vote,
  },
  {
    id: "construction-rules",
    tab: "rules",
    title: "Construction Rules",
    summary: "Reference building requirements for hours, trades, flooring, walls, and shutdowns.",
    keywords: "rules regulations plumbing electrical flooring washer dryer walls shutdown",
    icon: BookOpen,
  },
  {
    id: "fees",
    tab: "fees",
    title: "Fees and deposits",
    summary: "See construction deposits, shutdown charges, and insurance-related fines.",
    keywords: "fees deposit payment check fine shutdown refund",
    icon: FileCheck2,
  },
  {
    id: "admin-guide",
    tab: "admin",
    title: "Administrator tools",
    summary: "Manage portal configuration, users, invitations, buildings, and contractor records.",
    keywords: "admin users invitations buildings configuration import contractors",
    icon: ShieldCheck,
  },
  {
    id: "inspections",
    tab: "chief_engineer",
    title: "Record inspections",
    summary: "Follow phased flooring inspections and document field observations.",
    keywords: "chief engineer inspection cork plywood finish notes",
    icon: HardHat,
  },
];

export function HelpPage() {
  const { state } = useAuth();

  const myRoles = useMemo<RoleKey[]>(() => {
    if (state.status !== "authenticated") return [];
    const codes = new Set(state.me.roles.map((r) => r.code));
    const ordered: RoleKey[] = [
      "admin",
      "property_manager",
      "board_member",
      "chief_engineer",
      "unit_owner",
    ];
    return ordered.filter((r) => codes.has(r));
  }, [state]);

  const location = useLocation();
  const initialTab = ((): TabKey => {
    const hash = location.hash.replace(/^#/, "");
    const guideTopic = GUIDE_TOPICS.find((topic) => topic.id === hash);
    if (guideTopic) return guideTopic.tab;
    if (RULES_HASH_IDS.has(hash)) return "rules";
    if (FEES_HASH_IDS.has(hash)) return "fees";
    return "common";
  })();
  const [active, setActive] = useState<TabKey>(initialTab);
  const [query, setQuery] = useState("");

  // After the right tab is mounted, scroll the anchored card into
  // view. Browsers don't auto-scroll to anchors that exist only
  // after a render pass.
  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const guideTopic = GUIDE_TOPICS.find((topic) => topic.id === hash);
    if (
      (guideTopic && active !== guideTopic.tab) ||
      (RULES_HASH_IDS.has(hash) && active !== "rules") ||
      (FEES_HASH_IDS.has(hash) && active !== "fees")
    ) {
      return;
    }
    const t = window.setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [active, location.hash]);

  if (state.status !== "authenticated") {
    return (
      <div className="max-w-3xl">
        <PageHeader title="Help" />
        <Card>
          <p className="text-ink-500">
            You're not signed in. Contractor uploading via a magic link?{" "}
            <Link to="/help/contractor" className="text-brand-700">
              Contractor help
            </Link>
            .
          </p>
        </Card>
      </div>
    );
  }

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: "common", label: "Getting started" },
    { key: "rules", label: "Construction Rules" },
    { key: "fees", label: "Fees" },
    ...myRoles.map((r) => ({ key: r as TabKey, label: ROLE_LABEL[r] })),
  ];

  const availableTabs = new Set<TabKey>(["common", "rules", "fees", ...myRoles]);
  const visibleTopics = GUIDE_TOPICS.filter((topic) =>
    availableTabs.has(topic.tab),
  );
  const normalizedQuery = query.trim().toLowerCase();
  const searchTerms = normalizedQuery
    .split(/\s+/)
    .filter((term) => term.length >= 3);
  const searchResults = normalizedQuery
    ? visibleTopics.filter((topic) => {
        const searchable =
          `${topic.title} ${topic.summary} ${topic.keywords}`.toLowerCase();
        return (
          searchable.includes(normalizedQuery) ||
          searchTerms.some((term) => searchable.includes(term))
        );
      })
    : [];

  const openTopic = (topic: GuideTopic) => {
    setActive(topic.tab);
    setQuery("");
    window.history.replaceState(null, "", `/help#${topic.id}`);
    window.setTimeout(() => {
      document
        .getElementById(topic.id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const featuredTopics = visibleTopics.filter((topic) =>
    [
      "submit-project",
      "contractor-lookup",
      "management-review",
      "board-voting",
      "construction-rules",
      "fees",
    ].includes(topic.id),
  );

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Help Center"
        subtitle="Step-by-step guidance for using The Elm at Clark Construction Portal."
      />

      <section className="relative overflow-hidden rounded-xl bg-brand-900 text-white px-5 py-6 sm:px-8 sm:py-8 mb-5">
        <div className="absolute -right-12 -top-20 h-56 w-56 rounded-full border border-white/10" />
        <div className="absolute -right-4 -top-10 h-36 w-36 rounded-full border border-white/10" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-200 mb-2">
            <BookOpen size={14} />
            Portal user guide
          </div>
          <h2 className="text-white text-[28px] sm:text-[34px] mb-2">
            What can we help you do?
          </h2>
          <p className="text-white/70 text-[13px] sm:text-sm mb-5">
            Search instructions, browse guidance for your role, or download the
            complete guide for offline reference.
          </p>
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              type="search"
              className="w-full rounded-lg border-0 bg-white py-3 pl-11 pr-10 text-[14px] text-ink-800 shadow-lg outline-none focus:ring-2 focus:ring-brand-300"
              placeholder="Search projects, contractors, insurance, voting…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search the portal user guide"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-ink-400 hover:text-ink-700"
                aria-label="Clear guide search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </section>

      {normalizedQuery ? (
        <section className="mb-6" aria-live="polite">
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-ink-800">
              {searchResults.length}{" "}
              {searchResults.length === 1 ? "result" : "results"}
            </h3>
            <button
              type="button"
              onClick={() => setQuery("")}
              className="text-xs text-brand-700 hover:underline"
            >
              Clear search
            </button>
          </div>
          {searchResults.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-2.5">
              {searchResults.map((topic) => (
                <TopicButton
                  key={topic.id}
                  topic={topic}
                  onClick={() => openTopic(topic)}
                />
              ))}
            </div>
          ) : (
            <Card className="text-center py-8">
              <Search size={24} className="mx-auto text-ink-300 mb-2" />
              <p className="font-medium text-ink-700">No guide topics found</p>
              <p className="text-xs text-ink-500 mt-1">
                Try a broader term, or browse the role and reference tabs below.
              </p>
            </Card>
          )}
        </section>
      ) : (
        <section className="mb-6">
          <div className="flex items-end justify-between gap-4 mb-2.5">
            <div>
              <h3 className="text-ink-800">Popular guides</h3>
              <p className="text-xs text-ink-500 mt-0.5">
                Shortcuts are tailored to the roles on your account.
              </p>
            </div>
            <a
              href="/guides/portal-user-guide.pdf"
              download
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
            >
              <Download size={14} />
              Download PDF
            </a>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {featuredTopics.map((topic) => (
              <TopicButton
                key={topic.id}
                topic={topic}
                onClick={() => openTopic(topic)}
              />
            ))}
          </div>
          <a
            href="/guides/portal-user-guide.pdf"
            download
            className="sm:hidden mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-brand-200 bg-white px-4 py-2.5 text-[13px] font-medium text-brand-700"
          >
            <Download size={15} />
            Download complete PDF guide
          </a>
        </section>
      )}

      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-400">
          Browse the guide
        </span>
        <div className="h-px flex-1 bg-sand-200" />
      </div>
      <div className="flex gap-1 mb-5 overflow-x-auto no-scrollbar -mx-4 sm:-mx-1 px-4 sm:px-1 snap-x snap-mandatory">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={cx(
              "px-3.5 py-2 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors snap-start shrink-0",
              active === t.key
                ? "bg-white text-brand-700 border border-brand-200 shadow-soft"
                : "bg-transparent text-ink-600 hover:text-ink-800 hover:bg-white border border-transparent",
            )}
            onClick={() => setActive(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {active === "common" && <CommonSection />}
      {active === "rules" && <RulesSection />}
      {active === "fees" && <FeesSection />}
      {active === "unit_owner" && <OwnerSection />}
      {active === "property_manager" && <PropertyManagerSection />}
      {active === "admin" && <AdminSection />}
      {active === "board_member" && <BoardMemberSection />}
      {active === "chief_engineer" && <ChiefEngineerSection />}

      <p className="text-ink-500 text-xs mt-6">
        Need to contact the building? Property Management is your first point
        of contact for anything not covered here. Emergencies (burst pipe,
        electrical hazard) bypass approval — call building maintenance and
        retroactively log the project within 7 days.
      </p>
    </div>
  );
}

function TopicButton({
  topic,
  onClick,
}: {
  topic: GuideTopic;
  onClick: () => void;
}) {
  const Icon = topic.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-start gap-3 rounded-lg border border-sand-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-soft focus:outline-none focus-visible:shadow-ring"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700">
        <Icon size={17} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-ink-800">
          {topic.title}
        </span>
        <span className="mt-0.5 block text-[11.5px] leading-relaxed text-ink-500">
          {topic.summary}
        </span>
      </span>
      <ArrowRight
        size={14}
        className="mt-1 shrink-0 text-ink-300 transition group-hover:translate-x-0.5 group-hover:text-brand-600"
      />
    </button>
  );
}

function CommonSection() {
  return (
    <Card id="getting-started">
      <h2 className="text-ink-800 m-0 mb-2">Getting started</h2>
      <p className="text-ink-700">
        The portal coordinates every party to a construction project: the
        Owner, Property Management, the Board, and the Contractor team. Each
        role has its own queue and actions.
      </p>
      <h3 className="text-ink-800 mt-4">Working hours</h3>
      <ul className="list-disc pl-5 text-ink-700 space-y-1">
        <li>Construction: 9 AM – 5 PM, Monday–Friday only.</li>
        <li>Tradespeople may not enter the building before 7:30 AM.</li>
        <li>Off-hours work needs a 72-hour advance written request to Management.</li>
        <li>No work on holidays or weekends except declared emergencies.</li>
      </ul>
      <h3 className="text-ink-800 mt-4">Project categories</h3>
      <ul className="list-disc pl-5 text-ink-700 space-y-1">
        <li>
          <strong>Category A — Decorative:</strong> notification only. Paint,
          wallpaper, carpet. No deposit, no fees, no Board approval.
        </li>
        <li>
          <strong>Category B — System-Affecting:</strong> Board approval,
          $500 deposit, owner liability insurance on file, phased inspections.
        </li>
        <li>
          <strong>Category C — Common Element / Structural:</strong> Cat B +
          a signed License Agreement with the Association.
        </li>
      </ul>
      <div id="contractor-lookup" className="mt-5 border-t border-sand-100 pt-4 scroll-mt-24">
        <h3 className="text-ink-800">Find and share a contractor</h3>
        <ol className="list-decimal pl-5 text-ink-700 space-y-1 mt-2">
          <li>
            Open the{" "}
            <Link to="/contractors" className="text-brand-700">
              Contractors directory
            </Link>
            .
          </li>
          <li>Search by company, contact information, or type of work.</li>
          <li>
            Review project counts, ratings, and expandable prior job history.
          </li>
          <li>
            Select <strong>Share</strong> to send the contractor's available
            contact details through your device's share menu.
          </li>
        </ol>
      </div>
    </Card>
  );
}

function OwnerSection() {
  return (
    <>
      <Card id="submit-project">
        <h2 className="text-ink-800 m-0">Unit Owner</h2>
        <p className="text-ink-700">
          You are the project initiator. Submission is gated on a checklist
          that varies by category and type — the portal tells you what's
          missing in real time.
        </p>
      </Card>
      <Card id="insurance-guide">
        <h3 className="text-ink-800 m-0 mb-2">Submit a project</h3>
        <ol className="list-decimal pl-5 text-ink-700 space-y-1">
          <li>
            <Link to="/projects/new" className="text-brand-700">New project</Link> — pick a project type;
            the portal infers Category and prefills the requirement checklist.
          </li>
          <li>Add scope of work and proposed dates.</li>
          <li>Upload owner-side documents.</li>
          <li>Add contractors; the portal emails them a single-use upload link.</li>
          <li>Sign the Indemnity Agreement (binding on successors).</li>
          <li>Submit for review.</li>
        </ol>
      </Card>
      <Card id="management-review">
        <h3 className="text-ink-800 m-0 mb-2">Insurance you must keep on file</h3>
        <p className="text-ink-700">
          Per the Rules, every owner must keep a personal liability policy of
          at least <strong>$500,000 per incident</strong> on file. Use the{" "}
          <Link to="/my/insurance" className="text-brand-700">Insurance</Link>{" "}
          page to add or renew.
        </p>
        <p className="text-ink-700">
          <strong>Important:</strong> If a renewal is missed, a{" "}
          <strong>$100 fine</strong> is added on the 15th of each month
          following the lapse and recurs until coverage is restored.
        </p>
      </Card>
      <Card id="admin-guide">
        <h3 className="text-ink-800 m-0 mb-2">Fees</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li><strong>$500</strong> Construction Security Deposit (Cat B/C, refundable).</li>
          <li><strong>$150</strong> per riser shutdown · $20 per reschedule.</li>
          <li>Telecom shutdowns: first per system free, $100 each subsequent.</li>
          <li><strong>$100/mo</strong> missing-insurance fine — automatic.</li>
        </ul>
        <p className="text-xs text-ink-500 mt-2">
          The portal records fees as <em>due</em>; pay by check to{" "}
          <em>"The Elm at Clark Condominium Association"</em>. Management
          marks rows received once the check is in hand.
        </p>
      </Card>
    </>
  );
}

function PropertyManagerSection() {
  return (
    <>
      <Card id="board-voting">
        <h2 className="text-ink-800 m-0">Property Manager</h2>
        <p className="text-ink-700">
          You're the operational backbone — every project lands in your queue
          first.
        </p>
      </Card>
      <Card id="construction-rules">
        <h3 className="text-ink-800 m-0 mb-2">Daily flow</h3>
        <ol className="list-decimal pl-5 text-ink-700 space-y-1">
          <li>Open <Link to="/management" className="text-brand-700">Queue</Link>.</li>
          <li><strong>Start review</strong> on a Submitted project.</li>
          <li>If anything is missing, <strong>Return for revisions</strong> with a written reason.</li>
          <li>For Cat A, <strong>Approve administratively</strong>.</li>
          <li>For Cat B/C, <strong>Route to Board</strong>. The portal freezes the eligible voter pool and opens a 14-day window.</li>
        </ol>
      </Card>
      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Visibility</h3>
        <p className="text-ink-700">
          You see the full Board vote breakdown in real time — <em>who</em>{" "}
          voted and <em>how</em>. Board members do not see each other's
          votes; the design choice prevents anchoring bias during the
          asynchronous vote.
        </p>
      </Card>
    </>
  );
}

function AdminSection() {
  return (
    <>
      <Card>
        <h2 className="text-ink-800 m-0">Administrator</h2>
        <p className="text-ink-700">
          Admins inherit Property Manager capabilities and add system
          configuration powers.
        </p>
      </Card>
      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Quorum configuration</h3>
        <p className="text-ink-700">
          Defaults: majority participation, majority of voters, most-restrictive
          tie-breaker (Reject &gt; Revisions &gt; Conditional &gt; Approve),
          14-day window, auto-recuse unit owners.
        </p>
      </Card>
      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Common administrator tasks</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <Link to="/admin/users" className="text-brand-700">
              Manage user accounts and roles
            </Link>
          </li>
          <li>
            <Link to="/admin/invites" className="text-brand-700">
              Issue and review invite codes
            </Link>
          </li>
          <li>
            <Link to="/admin/insurance-review" className="text-brand-700">
              Review owner insurance
            </Link>
          </li>
          <li>
            <Link to="/admin/contractor-import" className="text-brand-700">
              Import the approved contractor directory
            </Link>
          </li>
        </ul>
      </Card>
    </>
  );
}

function BoardMemberSection() {
  return (
    <>
      <Card>
        <h2 className="text-ink-800 m-0">Board Member</h2>
        <p className="text-ink-700">
          You vote individually on Cat B / C projects. Aggregation runs against
          the building's quorum policy.
        </p>
      </Card>
      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Privacy</h3>
        <p className="text-ink-700">
          The system is <strong>blind</strong> by design: you can see your own
          vote and a count of votes cast, but never <em>how</em> other Board
          members voted, even after finalisation. Property Management sees the
          full breakdown.
        </p>
      </Card>
      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Tie-breaker</h3>
        <p className="text-ink-700">
          When two decisions tie at the top, default policy is{" "}
          <em>most restrictive</em>: Reject &gt; Revisions &gt; Conditional
          &gt; Approve.
        </p>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Construction Rules — building-wide reference, visible to every role.
// Curated from the building's Construction Rules and Regulations doc plus
// the seed scope templates so the same language shows up here, in the
// wizard checklists, and on the project review.
// ---------------------------------------------------------------------------
function RulesSection() {
  return (
    <>
      <Card>
        <h2 className="text-ink-800 m-0 mb-2">Construction Rules and Regulations</h2>
        <p className="text-ink-700">
          The Elm at Clark Condominium Association — applicable to every
          owner-initiated project. This page summarises the binding rules; the
          full document is available from Property Management on request.
        </p>
        <p className="text-ink-700">
          By submitting a project the owner acknowledges these rules and
          agrees to ensure every contractor and tradesperson abides by them.
          Violations may result in fines, deposit forfeiture, and stop-work
          orders.
        </p>
      </Card>

      <Card id="categories">
        <h3 className="text-ink-800 m-0 mb-2">Categories</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>Category A — Decorative.</strong> Paint, wallpaper, carpet
            install/remove. Notification only — no Board approval, no deposit.
          </li>
          <li>
            <strong>Category B — System-Affecting.</strong> Kitchen, bathroom,
            washer/dryer, hard-surface flooring, wall modification. Board
            approval, $500 deposit, owner liability insurance, phased
            inspection where applicable.
          </li>
          <li>
            <strong>Category C — Common Element / Structural.</strong> Cat B
            +&nbsp;a signed License Agreement with the Association. Combining
            units or structural work.
          </li>
        </ul>
      </Card>

      <Card id="working-hours">
        <h3 className="text-ink-800 m-0 mb-2">Working hours</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>Construction: 9 AM – 5 PM, Monday–Friday only.</li>
          <li>Tradespeople may not enter the building before 7:30 AM.</li>
          <li>Loading dock entry only — no main lobby for tools or materials.</li>
          <li>
            No work on holidays or weekends without an off-hours request
            submitted at least 72 hours in advance.
          </li>
          <li>Emergencies (burst pipe, electrical hazard) bypass approval; retroactively log within 7 days.</li>
        </ul>
      </Card>

      <Card id="insurance">
        <h3 className="text-ink-800 m-0 mb-2">Insurance</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>Owner personal liability:</strong> minimum $500,000 per
            incident, kept current at all times. Lapse triggers an automatic
            $100/month fine.
          </li>
          <li>
            <strong>Contractor:</strong> $1M General Liability, $1M Auto BI+PD,
            statutory Workers' Compensation. The Association, the Board, and
            Sudler must be listed as Additional Insured. 30 days cancellation
            notice required.
          </li>
        </ul>
      </Card>

      <Card id="plumbing-electrical">
        <h3 className="text-ink-800 m-0 mb-2">Plumbing &amp; electrical</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>All plumbing by a City of Chicago licensed plumber.</li>
          <li>All electrical by a City of Chicago licensed electrician.</li>
          <li>
            Individual shut-off valves and dielectric unions on every newly
            installed fixture.
          </li>
          <li>Drain runs over 5' from a fixture must be properly vented.</li>
          <li>GFCI receptacles per Chicago code.</li>
          <li>
            <strong>No relocation</strong> of water risers, waste lines, or
            vent stacks (prohibited).
          </li>
        </ul>
      </Card>

      <Card id="hard-surface-flooring">
        <h3 className="text-ink-800 m-0 mb-2">Hard-surface flooring</h3>
        <p className="text-ink-700">
          Wood, stone, and ceramic floor installations require underlayment
          per the building's Exhibit A (wood) or Exhibit B (tile):
        </p>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>1/4" cork laminated to slab with mastic.</li>
          <li>
            <strong>Wood:</strong> 3/4" fire-treated plywood on top of the
            cork; finish floor with nails not penetrating the cork.
          </li>
          <li>
            <strong>Tile:</strong> tile applied directly to the cork;
            elastomeric / acoustical sealant at the floor perimeter.
          </li>
          <li>
            Latex leveling within Rule limits: 3/4" max / 1/2" avg for light
            finishes; 1/2" max / 3/8" avg for heavy stone.
          </li>
          <li>
            <strong>Three-phase Chief Engineer inspection</strong> — sign-off
            required after cork, after plywood (wood only), and after finish
            before molding. Skipping a phase forfeits the deposit.
          </li>
        </ul>
      </Card>

      <Card id="washer-dryer">
        <h3 className="text-ink-800 m-0 mb-2">Washer / dryer install</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>Location near the kitchen waste riser only — no other riser permitted.</li>
          <li>
            <strong>Ventless electric dryers only.</strong> Vented dryers are
            prohibited.
          </li>
          <li>Leak-detection regulator with auto shut-off on hot/cold supply on malfunction.</li>
          <li>Code-approved 2" air gap on drain to building sanitary system.</li>
          <li>
            Vacuum breakers on each supply line; permanent wall valve boxes —
            no direct connections to existing supply faucets.
          </li>
          <li>Fabcel 200 (or equal) machinery mounting pads, min 6" sq, 1/2" thick.</li>
          <li>Low-suds / low-foam detergents required.</li>
          <li>Annual inspection by Chief Engineer.</li>
        </ul>
      </Card>

      <Card id="wall-modifications">
        <h3 className="text-ink-800 m-0 mb-2">Wall modifications</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>No load-bearing walls or shear walls may be affected.</li>
          <li>No channeling of support columns.</li>
          <li>
            Floor-slab channeling ≤ 1" depth or rebar depth, whichever is
            less; rebar must not be cut or disturbed.
          </li>
          <li>
            Demising walls between units must be modified to STC ≥ 54 where
            required.
          </li>
          <li>
            Architectural drawings sealed by a licensed Architect (where
            required by the Chicago Building Code).
          </li>
          <li>
            Pre-wall-close inspection by the Architectural Representative
            requested before any walls are closed.
          </li>
        </ul>
      </Card>

      <Card id="shutdowns">
        <h3 className="text-ink-800 m-0 mb-2">Riser &amp; telecom shutdowns</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>Water riser shutdown:</strong> 72-hour minimum advance
            notice; $150 fee on submission; $20 reschedule. On approval every
            owner in the affected stack is notified.
          </li>
          <li>
            <strong>Telecom / cable / antenna shutdown:</strong> first per
            system free, $100 each subsequent. Available 10 AM – 3 PM
            Monday–Friday only with 3 working days advance notice.
          </li>
        </ul>
      </Card>

      <Card id="agreements">
        <h3 className="text-ink-800 m-0 mb-2">Acknowledgements &amp; agreements</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>Construction Rules Acknowledgement:</strong> click-wrap on
            the project page. Owner acknowledges the rules; each contractor
            also acknowledges before submission.
          </li>
          <li>
            <strong>Procedures for Unit Construction Inspection:</strong>{" "}
            acknowledged by owner and contractors.
          </li>
          <li>
            <strong>Indemnity Agreement:</strong> click-wrap signature (typed
            full name + IP + timestamp + canonical-text hash). Required for
            Cat C and any contractor insurance deviation. Binding on
            successors in interest to the unit.
          </li>
          <li>
            <strong>Permission to Enter:</strong> contractors authorise
            building staff to inspect the work area.
          </li>
          <li>
            <strong>License Agreement:</strong> required for Cat C (combining
            units, structural). Two-party — owner + Association.
          </li>
        </ul>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Fees — single source of truth for every fee the portal tracks. Pulled
// straight from the Construction Rules and the fee-ledger config.
// ---------------------------------------------------------------------------
function FeesSection() {
  return (
    <>
      <Card id="fees">
        <h2 className="text-ink-800 m-0 mb-2">Fees</h2>
        <p className="text-ink-700">
          Every fee the portal tracks. The portal records each fee as{" "}
          <em>due</em>; payment is by check unless noted. Property Management
          marks rows received once the check is in hand.
        </p>
        <p className="text-ink-700">
          Make checks payable to{" "}
          <em>"The Elm at Clark Condominium Association"</em>.
        </p>
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Construction Security Deposit</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>$500</strong> per project, refundable.
          </li>
          <li>Charged on submission for Category B and Category C projects.</li>
          <li>
            Refunded after Property Management's closeout inspection confirms
            no common-element damage.
          </li>
          <li>
            <strong>Forfeited</strong> if a phased Chief Engineer inspection is
            skipped, or if an inspection reveals work the Rules prohibit.
          </li>
        </ul>
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Water riser shutdown</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>$150</strong> per shutdown — charged when the request is
            submitted.
          </li>
          <li>
            <strong>$20</strong> per reschedule.
          </li>
          <li>72-hour minimum advance notice required.</li>
        </ul>
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Telecom / cable / antenna shutdown</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>First per system: free.</strong> Each subsequent shutdown
            on the same system: <strong>$100</strong>.
          </li>
          <li>
            Available 10 AM – 3 PM, Monday – Friday only, with 3 working days
            advance notice.
          </li>
        </ul>
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Missing-insurance fine (automatic)</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            <strong>$100/month</strong> if an owner's $500K personal liability
            policy isn't on file by the 10th of the month following expiration.
          </li>
          <li>
            Charged automatically on the 15th and recurs every month until the
            owner uploads a current certificate via{" "}
            <Link to="/my/insurance" className="text-brand-700">
              My Insurance
            </Link>
            .
          </li>
          <li>
            Recorded in the fee ledger and a separate compliance-fines record.
          </li>
        </ul>
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-2">Other charges</h3>
        <ul className="list-disc pl-5 text-ink-700 space-y-1">
          <li>
            Damage to common elements is billed back at cost via Property
            Management; the deposit may be applied first.
          </li>
          <li>
            Stop-work re-inspection or re-issuance of approval after a
            violation may incur an administrative fee at the Board's
            discretion.
          </li>
        </ul>
        <p className="text-xs text-ink-500 mt-2">
          All portal-recorded fees are currently paid by check. Property
          Management updates the ledger after payment is received.
        </p>
      </Card>
    </>
  );
}

function ChiefEngineerSection() {
  return (
    <Card id="inspections">
      <h2 className="text-ink-800 m-0">Chief Engineer</h2>
      <p className="text-ink-700">
        Three-phase flooring inspection: cork → plywood → finish. Sign off
        before the next phase begins. Skipping a phase forfeits the deposit.
      </p>
      <div className="alert-info mt-3">
        Record each inspection result and field observation in the project's
        inspection notes before the next construction phase begins.
      </div>
    </Card>
  );
}
